import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit, signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {NgOptimizedImage} from "@angular/common";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {assets} from '@core/constants';
import {Coding} from '@core/classes/other/coding';
import {ATC} from '@core/classes/other/atc';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {lastValueFrom} from 'rxjs';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {SurgicalProcedure} from '@core/classes/tables/surgical-procedure';
import {checkDate} from '@core/helpers';
import moment from 'moment';

@Component({
  selector: 'ncp-add-surgical-procedure',
    imports: [
        ButtonComponent,
        DateComponent,
        DropdownComponent,
        ModalComponent,
        NgOptimizedImage,
        ReactiveFormsModule
    ],
  templateUrl: './add-surgical-procedure.component.html',
  styleUrl: './add-surgical-procedure.component.scss',
})
export class AddSurgicalProcedureComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('spFrm') protected proceduresFormEl?: NgForm;
  @ViewChild('addProcedureButton') public addProcedureButton!: ButtonComponent;

  @ViewChild( 'procedureDesc') public procedureDesc!: DropdownComponent<Coding>;
  @ViewChild('procedureDate') public procedureDate!: DateComponent;

  @ViewChild('addProcedureButton') getSPbtn  = () => {
    return this.addProcedureButton
  }

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | undefined>(undefined, [Validators.required]),
    procedure_date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let procedureDate = (control as FormGroup).controls['procedure_date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({sp_codingIdInvalid: true});
        errors['sp_codingIdInvalid'] = true;
      }

      if((!checkDate(procedureDate.value).isValid())) {
        procedureDate.setErrors({sp_procedureDateInvalid: true});
        errors['sp_procedureDateInvalid'] = true;
      } else {
        if(checkDate(procedureDate.value).isBefore(patientDOB)) {
          procedureDate.setErrors({dsp_procedureDateAfterDOB: true});
          errors['sp_procedureDateAfterDOB'] = true;
        } else if(checkDate(procedureDate.value).isSameOrAfter(today)) {
          procedureDate.setErrors({sp_procedureDateBeforeToday: true});
          errors['sp_procedureDateBeforeToday'] = true;
        } else {
          procedureDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  public btnSPOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_procedure',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_procedure',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected procedureDescDDOptions = signal<DropdownOptions<Coding | ATC>>(
    new DropdownOptions<Coding | ATC>({
      id: 'procedureDescription',
      name: 'procedureDescription',
      placeholder: 'procedure_description',
      label: {
        id: "procedure_desc_label",
        text: 'procedure_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.10',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected procedureDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'onSetDate',
      label: {
        text: 'on_set',
      },
      name: 'onSetDate',
      initialValue: '',
      formControl: this.form.get('procedure_date') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.btnSPOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      this.procedureDesc.detectChanges();
      this.addProcedureButton.blur();
      this.btnSPOptions().isLoading = true;
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.btnSPOptions().isLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    //this.procedureDateOptions().formControl = (this.form.get('procedure_date') as FormControl);
    this.modOptions().onSave = this.onSave;
    this.modOptions().onShow = this.downloadDropDownData;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.procedureDesc.clearSelection();
    this.procedureDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async (title?: string) => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('procedureDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.call({
      path: '/get-all_codings',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      body: {
        oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.10',
        description: ''
      }
    })!);

    if (data && data.status === 1 && data.data.length > 0) {
      Object.defineProperty(ddData, 'procedureDescription', {
        value: (data as Response<Coding>).data,
        enumerable: true,
        writable: false
      });
      this.procedureDesc.data = (data as Response<Coding>).data || [];

      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.proceduresFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      procedure_date: (this.form.get('procedure_date') as FormControl).value,
      coding_id: this.procedureDescDDOptions().value!.coding_id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/surgical-procedures',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<SurgicalProcedure>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
